import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

import {
  DEFAULT_AGENT_NAME,
  getCardClassName,
  getWrapperClassName,
} from './chat-agent-item-layout.config'
import { ChatBotAvatar } from './chat-bot-avatar.atom'
import { ChatPinButton } from './chat-pin-button.atom'
import { ChatTimestamp } from './chat-timestamp.atom'

const HIGHLIGHT_RING_CLASSES = 'chat-pin-flash'

export interface ChatAgentItemLayoutProps {
  itemId: string
  timestamp: string | Date
  agentName?: string
  isPinned?: boolean
  isHighlighted?: boolean
  onPin?: (itemId: string) => void
  onClick?: () => void
  cardClassName?: string
  wrapperClassName?: string
  className?: string
  children: ReactNode
}

/**
 * Shared layout for chat items that originate from an agent.
 * Provides consistent structure: avatar, agent name/timestamp, content card, and pin button.
 */
export const ChatAgentItemLayout: React.FC<ChatAgentItemLayoutProps> = ({
  itemId,
  timestamp,
  agentName = DEFAULT_AGENT_NAME,
  isPinned = false,
  isHighlighted = false,
  onPin,
  onClick,
  cardClassName,
  wrapperClassName,
  className,
  children,
}): ReactNode => (
  <div
    data-highlighted={isHighlighted ? 'true' : undefined}
    className={getWrapperClassName(wrapperClassName, className)}
  >
    <div className="flex-shrink-0 pt-1">
      <ChatBotAvatar />
    </div>

    <div
      className={cn(
        'flex min-w-0 w-[67%] max-w-[75%] flex-col gap-1 px-1',
        isHighlighted && HIGHLIGHT_RING_CLASSES
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">{agentName}</span>
        <ChatTimestamp timestamp={timestamp} />
      </div>

      <div className="relative flex min-w-0 items-center">
        {onClick ? (
          <button type="button" className={getCardClassName(cardClassName, true)} onClick={onClick}>
            {children}
          </button>
        ) : (
          <div className={getCardClassName(cardClassName, false)}>{children}</div>
        )}
        {onPin && (
          <ChatPinButton
            itemId={itemId}
            isPinned={isPinned}
            onPin={onPin}
            className="absolute -right-8 top-1/2 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100"
          />
        )}
      </div>
    </div>
  </div>
)

ChatAgentItemLayout.displayName = 'ChatAgentItemLayout'
